import type { DerivedSnapshot } from "@/lib/derivedIntel/derivedIntelligenceEngine";

/**
 * Heat Window Engine — detects "hot windows" in the match: pressure cycles,
 * consecutive attacks, extreme risk, critical momentum. Deterministic.
 */

export type HeatWindowKind =
  | "pressure_cycle"
  | "attack_burst"
  | "extreme_risk"
  | "critical_momentum"
  | "goal_danger";

export interface HeatWindow {
  id: string;
  kind: HeatWindowKind;
  active: boolean;
  intensity: number; // 0..1
  label: string;
  detail: string;
}

export function detectHeatWindows(snap: DerivedSnapshot): HeatWindow[] {
  const out: HeatWindow[] = [];
  const w = snap.windows.w2m;
  const s = snap.signals;

  const pressurePeak = Math.max(w.avgPressureHome, w.avgPressureAway);
  if (pressurePeak > 0.55) {
    out.push({
      id: "press-cycle",
      kind: "pressure_cycle",
      active: pressurePeak > 0.7,
      intensity: pressurePeak,
      label: "HEAT WINDOW ACTIVE",
      detail: `Pressure cycle running at ${Math.round(pressurePeak * 100)}%`,
    });
  }
  const burst = Math.max(w.attackBurstHome, w.attackBurstAway);
  if (burst >= 3) {
    out.push({
      id: "atk-burst",
      kind: "attack_burst",
      active: burst >= 4,
      intensity: Math.min(1, burst / 6),
      label: "Critical attack cycle detected",
      detail: `${burst} consecutive high-pressure samples`,
    });
  }
  const risk = Math.max(s.defensiveRiskHome, s.defensiveRiskAway);
  if (risk > 0.65) {
    out.push({
      id: "risk",
      kind: "extreme_risk",
      active: risk > 0.8,
      intensity: risk,
      label: "Danger pressure rising",
      detail: `Defensive risk index ${Math.round(risk * 100)}%`,
    });
  }
  if (Math.abs(s.momentumShift) > 0.5) {
    out.push({
      id: "mom",
      kind: "critical_momentum",
      active: Math.abs(s.momentumShift) > 0.7,
      intensity: Math.min(1, Math.abs(s.momentumShift)),
      label: "Critical momentum shift",
      detail: `Δ ${s.momentumShift.toFixed(2)} between 2m and 5m windows`,
    });
  }
  const goalProb = Math.max(s.goalProbabilityHome, s.goalProbabilityAway);
  if (goalProb > 0.6) {
    out.push({
      id: "goal",
      kind: "goal_danger",
      active: goalProb > 0.75,
      intensity: goalProb,
      label: "Goal danger imminent",
      detail: `Offensive odds ${Math.round(goalProb * 100)}%`,
    });
  }
  return out.sort((a, b) => b.intensity - a.intensity);
}